import type { FieldAccessLevel, FormField } from '@/types/api/form'

import { isButtonField } from './buttonField'
import { isLayoutField } from './layoutField'

/** What the signed-in user may do with a field. A server that does not report it allows everything. */
export function fieldAccessLevel(field: Pick<FormField, 'myAccess'>): FieldAccessLevel {
  return field.myAccess ?? 'EDIT'
}

export function canViewField(field: Pick<FormField, 'myAccess'>): boolean {
  return fieldAccessLevel(field) !== 'NONE'
}

export function canEditField(field: Pick<FormField, 'myAccess'>): boolean {
  return fieldAccessLevel(field) === 'EDIT'
}

/** Buttons and layout elements hold no value, so they are left out of everything that lists, filters or fills in values. */
export function isDataField(field: { type?: string }): boolean {
  return !isButtonField(field) && !isLayoutField(field)
}

/** The fields the user can see, for tables, filters, sorting and pickers. */
export function viewableFields<T extends Pick<FormField, 'myAccess'> & { type?: string }>(fields: readonly T[]): T[] {
  return fields.filter((field) => canViewField(field) && isDataField(field))
}

/** The fields the user can fill in, for the create form. */
export function editableFields<T extends Pick<FormField, 'myAccess'> & { type?: string }>(fields: readonly T[]): T[] {
  return fields.filter((field) => canEditField(field) && isDataField(field))
}

export const FIELD_VIEW_ONLY_HINT = 'You can view this field but not change it'
export const FIELD_NO_ACCESS_HINT = "You don't have access to this field"

/** What the create form shows: the fields the user can fill in, plus layout elements that shape the form. */
export function fillInFields<T extends Pick<FormField, 'myAccess'> & { type?: string }>(fields: readonly T[]): T[] {
  return fields.filter((field) => isLayoutField(field) || (canEditField(field) && isDataField(field)))
}
